import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DateField } from "@/components/DateTimeField";
import { AppHeader } from "@/components/AppHeader";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = {
  customer_name: "",
  customer_id: null,
  project_name: "",
  slab_color: "",
  sink: "",
  sink_location: "Warehouse",
  sink_notes: "",
  install_date: "",
  length_in: "",
  width_in: "",
  quantity: 1,
  status: "Reserved",
  comments: "",
};

const SINKS = [
  "None",
  "Undermount Single",
  "Undermount Double",
  "Farmhouse",
  "Vessel",
  "Drop-in",
  "Integrated Stone",
  "Bar / Prep",
];

export default function ReservationForm() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const customerId = params.get("customer");
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (id) return;
    if (!customerId) return;
    api
      .get(`/customers/${customerId}`)
      .then(({ data }) =>
        setForm((f) => ({
          ...f,
          customer_name: data.name,
          customer_id: data.id,
        })),
      );
  }, [id, customerId]);

  useEffect(() => {
    if (!id) return;
    api.get(`/reservations/${id}`).then(({ data }) =>
      setForm({
        ...EMPTY,
        ...data,
        length_in: data.length_in ?? "",
        width_in: data.width_in ?? "",
        install_date: data.install_date ?? "",
      }),
    );
  }, [id]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const payload = {
      ...form,
      quantity: Number(form.quantity) || 1,
      length_in: form.length_in === "" ? null : Number(form.length_in),
      width_in: form.width_in === "" ? null : Number(form.width_in),
      install_date: form.install_date || null,
    };
    delete payload.id;
    delete payload.tag;
    delete payload.created_at;
    delete payload.updated_at;
    delete payload.created_by;
    delete payload.updated_by;
    delete payload.photos;
    try {
      const { data } = id
        ? await api.put(`/reservations/${id}`, payload)
        : await api.post("/reservations", payload);
      toast.success(
        id ? "Reservation updated" : `Reservation ${data.tag} created`,
      );
      navigate(`/reservation/${data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  const field = (label, key, props = {}) => (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-widest text-zinc-400">
        {label}
      </Label>
      <Input
        data-testid={`${key.replace(/_/g, "-")}-input`}
        value={form[key]}
        onChange={(e) => set(key)(e.target.value)}
        className="h-12 border-zinc-800 bg-zinc-900 text-base"
        {...props}
      />
    </div>
  );

  return (
    <div className="grain min-h-screen">
      <AppHeader title={id ? "Edit" : "New"} />
      <main className="relative z-10 mx-auto max-w-3xl px-4 pb-32 pt-6">
        <button
          onClick={() => navigate(-1)}
          data-testid="back-button"
          className="mb-4 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors duration-150 hover:text-white"
        >
          <ArrowLeft size={16} weight="bold" /> Back
        </button>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          {id ? "Edit Reservation" : "Reserve a Slab"}
        </h1>

        <form
          onSubmit={submit}
          className="mt-8 space-y-5"
          data-testid="reservation-form"
        >
          {field("Customer Name", "customer_name", {
            required: true,
            placeholder: "Jane Doe",
          })}
          {field("Project Name", "project_name", {
            required: true,
            placeholder: "Kitchen remodel — Elm St",
          })}
          {field("Slab Color", "slab_color", {
            required: true,
            placeholder: "Calacatta Gold",
          })}

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Sink
            </Label>
            <Select value={form.sink || ""} onValueChange={set("sink")}>
              <SelectTrigger
                data-testid="sink-select"
                className="h-12 border-zinc-800 bg-zinc-900 text-base"
              >
                <SelectValue placeholder="Select sink type" />
              </SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-900">
                {SINKS.map((s) => (
                  <SelectItem
                    key={s}
                    value={s}
                    data-testid={`sink-option-${s}`}
                  >
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Sink Location
              </Label>
              <Select
                value={form.sink_location}
                onValueChange={set("sink_location")}
              >
                <SelectTrigger
                  data-testid="sink-location-select"
                  className="h-12 border-zinc-800 bg-zinc-900 text-base"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  {["Warehouse", "On-Site"].map((l) => (
                    <SelectItem
                      key={l}
                      value={l}
                      data-testid={`sink-location-${l}`}
                    >
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Sink Notes (model # / description)
              </Label>
              <Input
                data-testid="sink-notes-input"
                value={form.sink_notes}
                onChange={(e) => set("sink_notes")(e.target.value)}
                placeholder="Blanco 440150 · 30in single bowl"
                className="h-12 border-zinc-800 bg-zinc-900 text-base"
              />
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Install Date
              </Label>
              <DateField
                testId="install-date-input"
                value={form.install_date || ""}
                onChange={set("install_date")}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Status
              </Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger
                  data-testid="status-select"
                  className="h-12 border-zinc-800 bg-zinc-900 text-base"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  {["Reserved", "Fabricating", "Installed"].map((s) => (
                    <SelectItem
                      key={s}
                      value={s}
                      data-testid={`status-option-${s}`}
                    >
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            {field("Length (in)", "length_in", {
              type: "number",
              step: "0.1",
              placeholder: "126",
            })}
            {field("Width (in)", "width_in", {
              type: "number",
              step: "0.1",
              placeholder: "63",
            })}
            {field("Quantity", "quantity", { type: "number", min: "1" })}
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Fabrication Comments
            </Label>
            <Textarea
              data-testid="comments-input"
              value={form.comments}
              onChange={(e) => set("comments")(e.target.value)}
              rows={5}
              placeholder="Edge profile, cutouts, seam notes, template details…"
              className="border-zinc-800 bg-zinc-900 text-base"
            />
          </div>

          {error && (
            <p
              data-testid="form-error"
              className="text-sm font-medium text-red-400"
            >
              {error}
            </p>
          )}

          <div className="no-print fixed bottom-0 left-0 right-0 border-t border-zinc-800 bg-zinc-950 p-3 sm:static sm:border-0 sm:bg-transparent sm:p-0">
            <Button
              data-testid="save-reservation-button"
              type="submit"
              disabled={busy}
              className="h-12 w-full bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400 sm:w-auto sm:px-10"
            >
              {busy ? "Saving…" : id ? "Save Changes" : "Create Reservation"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
